/**
 * MedicationsTab.tsx
 *
 * The patient chart's Medications tab: the patient's medication list, active
 * first. Staff add a medication with the button above the list and open one
 * to edit, stop or remove it (MedicationEditModal).
 *
 * Rows a ScriptSure prescription added are marked "E-prescribed"; their drug
 * details are read-only.
 *
 * Not the free-text "Current medications" answer in Health Information —
 * that answer is separate and unchanged.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Badge, Button, Spinner } from '@/components/ui';
import { formatDate } from '@/lib/utils';
import {
  listPatientMedications,
  patientMedicationsKey,
  type PatientMedication,
} from '@/services/patient-medications.service';
import { MedicationEditModal } from './MedicationEditModal';

interface MedicationsTabProps {
  patientId: number;
}

export function MedicationsTab({ patientId }: MedicationsTabProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: patientMedicationsKey(patientId),
    queryFn: () => listPatientMedications(patientId),
  });

  // `editing` drives the modal:
  //   undefined → closed.
  //   null      → add a new medication.
  //   object    → edit that medication.
  const [editing, setEditing] = useState<PatientMedication | null | undefined>(undefined);

  const rows = data ?? [];

  return (
    <div className="space-y-4 py-2">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Medications</h2>
        <Button size="sm" onClick={() => setEditing(null)} leftIcon={<Plus className="h-4 w-4" />}>
          Add medication
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : isError ? (
        <p className="py-4 text-sm text-red-600">
          Couldn't load medications. Try again, and contact support if it keeps happening.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-sm text-slate-400 italic">No medications yet</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Medication</th>
                <th className="px-4 py-3">Directions</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Prescriber</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((med) => (
                <tr
                  key={med.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => setEditing(med)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{med.medicationName}</div>
                    {med.strength && <div className="text-slate-500">{med.strength}</div>}
                    {med.source === 'eprescribed' && (
                      <Badge variant="info" size="sm" className="mt-1">E-prescribed</Badge>
                    )}
                    {med.kind === 'supplement' && (
                      <Badge variant="neutral" size="sm" className="mt-1">Supplement</Badge>
                    )}
                    {med.kind === 'peptide' && (
                      <Badge variant="neutral" size="sm" className="mt-1">Peptide</Badge>
                    )}
                    {med.source === 'patient' && (
                      <Badge variant="warning" size="sm" className="mt-1">Patient-reported</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{med.directions || '—'}</td>
                  <td className="px-4 py-3 text-slate-700">{formatDate(med.startDate)}</td>
                  <td className="px-4 py-3">
                    {med.status === 'active' ? (
                      <Badge variant="success" size="sm">Active</Badge>
                    ) : (
                      <>
                        <Badge variant="neutral" size="sm">Stopped</Badge>
                        {med.endDate && (
                          <div className="mt-1 text-xs text-slate-500">{formatDate(med.endDate)}</div>
                        )}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-700">{med.prescriberName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && (
        <MedicationEditModal patientId={patientId} medication={editing} onClose={() => setEditing(undefined)} />
      )}
    </div>
  );
}
